export type BillingPeriod = "one-time" | "monthly";

export type PricePresentation = "on-request" | "from";

export interface PricingTierInput {
  priceConfirmed?: boolean | null;
  amount?: number | null;
  amountMax?: number | null;
  period?: BillingPeriod | string | null;
  /** "from" shows "from $X" once confirmed, and "TBD" until then. */
  presentation?: PricePresentation | null;
}

export interface PublicPrice {
  confirmed: boolean;
  label: string;
}

const ON_REQUEST = "Pricing on request";
const TBD = "TBD";

function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Canonical website package prices, agreed in the andrewleger.com redesign.
 * Fallback tiers below use these amounts as confirmed prices. A Sanity tier
 * marked confirmed overrides them; an unconfirmed Sanity tier keeps these.
 * The AI-ready bundle is Pro plus the AI assistant ($1,500) and lead
 * automation ($1,000), minus the existing $500 bundle discount.
 */
export const websitePackageAmounts = {
  starter: 1500,
  pro: 3000,
  careMonthly: 150,
  careAiMonthly: 249,
  aiReadyBundle: 5000,
} as const;

export const websitePackageLabels = {
  starter: formatUsd(websitePackageAmounts.starter),
  pro: formatUsd(websitePackageAmounts.pro),
  care: `${formatUsd(websitePackageAmounts.careMonthly)} a month`,
  careAi: `${formatUsd(websitePackageAmounts.careAiMonthly)} a month`,
  aiReadyBundle: formatUsd(websitePackageAmounts.aiReadyBundle),
};

/**
 * Public price label. Dollar amounts render only when Sanity has marked the
 * tier confirmed and stored a finite amount.
 * "from" offers show "from $X" when confirmed and "TBD" until then.
 */
export function publicPriceLabel(tier: PricingTierInput): PublicPrice {
  const presentation = tier.presentation === "from" ? "from" : "on-request";
  const amount = tier.amount;
  const confirmed =
    tier.priceConfirmed === true &&
    typeof amount === "number" &&
    Number.isFinite(amount) &&
    amount >= 0;

  if (!confirmed) {
    return { confirmed: false, label: presentation === "from" ? TBD : ON_REQUEST };
  }

  const max = tier.amountMax;
  const hasMax = typeof max === "number" && Number.isFinite(max) && max > amount;
  const formatted = hasMax ? `${formatUsd(amount)} to ${formatUsd(max)}` : formatUsd(amount);

  if (presentation === "from") {
    if (tier.period === "monthly" && !hasMax) {
      return { confirmed: true, label: `from ${formatUsd(amount)} per month` };
    }
    return { confirmed: true, label: `from ${formatted}` };
  }

  if (tier.period === "monthly") {
    return { confirmed: true, label: `${formatUsd(amount)} per month` };
  }

  return { confirmed: true, label: formatted };
}

/**
 * Short price token for tight slots (package cards, add-ons, order summary):
 * the bare amount once confirmed, "TBD" until then. No "from" or period
 * wording, so callers can add their own prefix or "/ month" suffix.
 */
export function shortPriceLabel(tier: PricingTierInput): PublicPrice {
  const amount = tier.amount;
  const confirmed =
    tier.priceConfirmed === true &&
    typeof amount === "number" &&
    Number.isFinite(amount) &&
    amount >= 0;
  if (!confirmed) return { confirmed: false, label: TBD };
  const max = tier.amountMax;
  const hasMax = typeof max === "number" && Number.isFinite(max) && max > amount;
  return { confirmed: true, label: hasMax ? `${formatUsd(amount)} to ${formatUsd(max)}` : formatUsd(amount) };
}

export interface LocalPackage {
  key: string;
  name: string;
  audience: string;
  /** Short "Best for ..." line for the package picker. */
  bestFor?: string;
  /** One-sentence description for add-on cards. */
  shortDescription?: string;
  bullets: string[];
  period: BillingPeriod;
  priceConfirmed: boolean;
  presentation?: PricePresentation;
  amount?: number | null;
  amountMax?: number | null;
}

/**
 * Page fallback when Sanity has no published local tiers.
 * Prices are the agreed redesign prices (websitePackageAmounts).
 */
export const localPackageFallbacks: LocalPackage[] = [
  {
    key: "starter",
    name: "Starter",
    audience: "Owner-operators and small crews who need a solid, professional presence.",
    bestFor: "Best for owner-operators and small crews.",
    period: "one-time",
    priceConfirmed: true,
    amount: websitePackageAmounts.starter,
    bullets: [
      "Mobile-friendly website, up to 5 pages (Home, Services, About, Service Area, Contact)",
      "Online booking or a quote request sent to your email or phone",
      "Click-to-call and click-to-text buttons",
      "Google Business Profile setup or cleanup: categories, hours, service area, photos, booking link",
      "Basic local SEO: page titles, descriptions, and Google-friendly business info",
      "Connected to your domain, with SSL (the padlock)",
      "One round of revisions",
      "A short walkthrough so you know how it all works",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    audience: "Growing companies that want more calls from more neighborhoods.",
    bestFor: "Best for growing companies.",
    period: "one-time",
    priceConfirmed: true,
    amount: websitePackageAmounts.pro,
    bullets: [
      "Everything in Starter",
      "Up to 12 pages, including a page for each main service",
      "Service-area pages for the parts of the metro you serve (for example Omaha, Papillion, Bellevue, Elkhorn, and Council Bluffs)",
      "Online booking with your availability, confirmations, and reminders",
      "Review request setup: an easy link customers can use to leave a Google review",
      "Google Analytics and Search Console so you can see where calls and bookings come from",
      "Photo gallery for recent jobs",
      "Two rounds of revisions",
    ],
  },
  {
    key: "care",
    name: "Care plan",
    audience: "Optional monthly care: hosting, updates, and small edits after launch.",
    shortDescription:
      "Hosting, security and updates, small monthly edits with a limit agreed up front, and uptime monitoring. Starts at launch. Cancel any time with written notice.",
    period: "monthly",
    presentation: "from",
    priceConfirmed: true,
    amount: websitePackageAmounts.careMonthly,
    bullets: [
      "Hosting, security, and software updates",
      "Small edits each month (new photos, prices, hours, seasonal promos). We agree the monthly limit up front.",
      "Uptime monitoring, so I find out if the site goes down and fix it",
      "Cancel any time. The notice period is written into the care plan.",
    ],
  },
  {
    key: "care-ai",
    name: "Care + AI",
    audience: "Everything in Care, plus upkeep for your site's AI chat or automations.",
    shortDescription:
      "Everything in Care, plus monitoring and tuning for your site’s AI chat or automations, and a monthly report on usage and cost. Starts at launch.",
    period: "monthly",
    presentation: "from",
    priceConfirmed: true,
    amount: websitePackageAmounts.careAiMonthly,
    bullets: [
      "Everything in the Care plan",
      "Monitoring and tuning for your site's AI chat or automations",
      "A monthly report on usage and cost",
    ],
  },
];

export const localTimeline = [
  {
    label: "Day 1",
    title: "Kickoff call (30 min)",
    body: "Your services, area, photos, and how you like to take bookings.",
  },
  {
    label: "Days 2 to 7",
    title: "Build",
    body: "I write and build the site, and set up booking and your Google profile.",
  },
  {
    label: "Days 8 to 10",
    title: "Review",
    body: "You look it over, and I make your revisions.",
  },
  {
    label: "Days 11 to 14",
    title: "Launch",
    body: "The site goes live on your domain, the Google profile is updated, and we do a quick walkthrough.",
  },
];

export const localFaqs = [
  {
    question: "Do I need to already have a website or domain?",
    answer:
      "No. If you have one, I'll use it. If not, I'll help you register one. Domain registration is a small yearly fee paid to the registrar; I'll help you set it up.",
  },
  {
    question: "Do you only build for service businesses?",
    answer:
      "No. I also build business websites, Shopify stores, and sites from your Figma files, in Omaha or anywhere.",
  },
  {
    question: "Do I own the website?",
    answer:
      "Yes. Your domain, your content, and your Google profile. If you ever leave the care plan, I'll help you move the site.",
  },
  {
    question: "What booking system do you use?",
    answer:
      "It depends on how you work. It can be a simple request form, a scheduling tool, or the booking system you already use. We'll pick that on the kickoff call.",
  },
  {
    question: "Can you guarantee I'll show up first on Google?",
    answer:
      "No one honestly can. I'll set you up the right way: a complete Google profile, a fast site, and clear service and area pages. That gives you the best shot.",
  },
  {
    question: "Do I have to sign up for the care plan?",
    answer:
      "No. It's optional. Without it, you get the finished site and handle hosting and later changes yourself. We'll talk through hosting before you decide.",
  },
  {
    question: "How does payment work?",
    answer:
      "We'll agree the terms before any work starts. A common split is half to start and half at launch.",
  },
  {
    question: "Are you local?",
    answer: "Yes. I'm in Omaha and happy to meet in person.",
  },
];

export interface SanityPricingTier {
  key?: string | null;
  name?: string | null;
  audience?: string | null;
  bullets?: string[] | null;
  amount?: number | null;
  amountMax?: number | null;
  period?: string | null;
  priceConfirmed?: boolean | null;
  priceStyle?: string | null;
}

export function resolveLocalPackages(tiers: SanityPricingTier[] | null | undefined): LocalPackage[] {
  if (!tiers || tiers.length === 0) return localPackageFallbacks;

  const resolved = tiers
    .filter((tier) => tier.name && tier.bullets && tier.bullets.length > 0)
    .map((tier, index): LocalPackage => {
      const key = tier.key || `tier-${index}`;
      const fallback = localPackageFallbacks.find((item) => item.key === key);
      const sanityConfirmed = tier.priceConfirmed === true && typeof tier.amount === "number";
      // An unconfirmed Sanity tier keeps the agreed fallback price.
      const useFallbackPrice = !sanityConfirmed && fallback?.priceConfirmed === true;
      return {
        key,
        name: tier.name as string,
        audience: tier.audience || "",
        bestFor: fallback?.bestFor,
        shortDescription: fallback?.shortDescription,
        bullets: (tier.bullets || []).filter((bullet): bullet is string => Boolean(bullet)),
        period: tier.period === "monthly" ? "monthly" : "one-time",
        presentation:
          tier.priceStyle === "from" || tier.period === "monthly" ? "from" : "on-request",
        priceConfirmed: useFallbackPrice ? true : tier.priceConfirmed === true,
        amount: useFallbackPrice
          ? fallback?.amount ?? null
          : typeof tier.amount === "number"
            ? tier.amount
            : null,
        amountMax: useFallbackPrice
          ? fallback?.amountMax ?? null
          : typeof tier.amountMax === "number"
            ? tier.amountMax
            : null,
      };
    });

  return resolved.length > 0 ? resolved : localPackageFallbacks;
}
